import { CalendarSearch, Landmark } from 'lucide-react'
import { Icon } from '../../platform/icons/Icon'
import type { LocalDate } from '../../platform/localDate'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { ACCOUNTS_PATH } from '../accounts/accountPaths'
import { periodTitle, type Granularity } from './cashflowPeriod'

type Props =
  | { kind: 'no-accounts' }
  | {
      kind: 'nothing-in-period'
      /** The selected period's first day. */
      period: LocalDate
      granularity: Granularity
    }

const PENDING = 'Pending transactions appear once they post.'

const NOUN: Record<Granularity, string> = { month: 'month', quarter: 'quarter', year: 'year' }

/**
 * Cashflow's two empty states (M3-R28), per `.agent/reports/m3-shape-empty-states.md`.
 * No accounts replaces everything below the header. Nothing in the selected
 * period replaces only the tiles and lists, and carries no action of its own:
 * for premium the chart stays, so another bar is one click away; a free user
 * has no chart, and the header's Previous / Next arrows are the way out.
 *
 * The no-accounts primary action is legal only because the Net HeroTile is not
 * on screen then (rule 9: lime fills the hero OR the primary action).
 */
export function CashflowEmpty(props: Props) {
  const block = useBreakpoint() === 'phone'
  const actionClass = `cf-empty__action${block ? ' cf-empty__action--block' : ''}`
  const buttonClass = block ? 'cf-block-action' : undefined

  if (props.kind === 'no-accounts') {
    return (
      <Panel className="cf-empty">
        <span className="cf-empty__icon">
          <Icon glyph={Landmark} size={24} />
        </span>
        <h2 className="cf-empty__title">
          Connect an account to see your cashflow
        </h2>
        <p className="note cf-empty__body">
          {"Cashflow is worked out from your accounts' posted transactions. Access is read-only — TrackE5 never moves money."}
        </p>
        <span className={actionClass}>
          <ButtonLink to={ACCOUNTS_PATH} variant="primary" size="panel" className={buttonClass}>
            Go to Accounts
          </ButtonLink>
        </span>
      </Panel>
    )
  }

  return (
    <Panel className="cf-empty">
      <span className="cf-empty__icon">
        <Icon glyph={CalendarSearch} size={24} />
      </span>
      <h2 className="cf-empty__title">
        {`Nothing moved in ${periodTitle(props.period, props.granularity)}.`}
      </h2>
      <p className="note cf-empty__body">
        {`No posted transactions in this ${NOUN[props.granularity]}. ${PENDING}`}
      </p>
    </Panel>
  )
}
